import { useEffect, useState } from 'react';
import { detectOS, isMobileDevice } from './detector';
import { isScreenshotPredecessor } from './keys';

/**
 * Strategy for detecting when the user leaves the browser window
 * - 'off':              disabled
 * - 'blur':             fires on window.blur (most aggressive -- Cmd+Tab, clicking another window, etc.)
 * - 'visibilitychange': fires on document.visibilitychange when the tab becomes hidden
 */
export type WindowFocusProtectionLevel = 'off' | 'blur' | 'visibilitychange';

export interface IUseScreenshotDeterrenceOptions {
    /** Show the keyboard overlay when a screenshot shortcut's modifier keys are held */
    keyboardProtectionEnabled: boolean;
    /** Whether and how to show the focus overlay when the user leaves the page */
    windowFocusProtectionLevel: WindowFocusProtectionLevel;
}

interface IScreenshotDeterrenceState {
    /** A screenshot shortcut's modifier keys were pressed */
    isKeyboardOverlayShown: boolean;
    /** The user left the window or tab */
    isFocusOverlayShown: boolean;
}

/**
 * Ignore keyups that are part of a system screenshot shortcut. e.g. Win+Shift+S fires
 * keyup {key:'S', metaKey:true} back to the browser after the OS handles it -- that must not
 * dismiss the overlay.
 */
const isScreenshotShortcutKeyUp = (event: KeyboardEvent) =>
    event.metaKey || (event.ctrlKey && event.shiftKey);

/**
 * Tracks when to show the desktop screenshot deterrence overlays. The keyboard overlay shows
 * the moment a screenshot shortcut's modifiers are held (before the OS takes the full combo) and
 * dismisses on the next real keyup. The focus overlay shows while the user is away from the page
 * and dismisses on return, click, or keyup.
 */
const useScreenshotDeterrence = ({
    keyboardProtectionEnabled,
    windowFocusProtectionLevel
}: IUseScreenshotDeterrenceOptions): IScreenshotDeterrenceState => {
    const [isKeyboardOverlayShown, setIsKeyboardOverlayShown] = useState(false);
    const [isFocusOverlayShown, setIsFocusOverlayShown] = useState(false);

    useEffect(() => {
        if (!keyboardProtectionEnabled) {
            return;
        }

        const os = detectOS();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (isScreenshotPredecessor(event, os)) {
                setIsKeyboardOverlayShown(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [keyboardProtectionEnabled]);

    useEffect(() => {
        const show = () => setIsFocusOverlayShown(true);
        const hide = () => setIsFocusOverlayShown(false);

        if (windowFocusProtectionLevel === 'blur' && !isMobileDevice()) {
            window.addEventListener('blur', show);
            window.addEventListener('focus', hide);

            return () => {
                window.removeEventListener('blur', show);
                window.removeEventListener('focus', hide);
            };
        }

        if (windowFocusProtectionLevel === 'visibilitychange') {
            const handleVisibilityChange = () => (document.hidden ? show() : hide());

            document.addEventListener('visibilitychange', handleVisibilityChange);

            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }

        return undefined;
    }, [windowFocusProtectionLevel]);

    useEffect(() => {
        if (!isKeyboardOverlayShown) {
            return;
        }

        const handleKeyUp = (event: KeyboardEvent) => {
            if (!isScreenshotShortcutKeyUp(event)) {
                setIsKeyboardOverlayShown(false);
            }
        };

        document.addEventListener('keyup', handleKeyUp);

        return () => document.removeEventListener('keyup', handleKeyUp);
    }, [isKeyboardOverlayShown]);

    useEffect(() => {
        if (!isFocusOverlayShown) {
            return;
        }

        const dismiss = () => setIsFocusOverlayShown(false);

        const handleKeyUp = (event: KeyboardEvent) => {
            if (!isScreenshotShortcutKeyUp(event)) {
                dismiss();
            }
        };

        document.addEventListener('click', dismiss);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('click', dismiss);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [isFocusOverlayShown]);

    return { isKeyboardOverlayShown, isFocusOverlayShown };
};

export default useScreenshotDeterrence;
